// src/features/license/LicenseDetailRail.tsx
// One license, three jobs: see what it grants (Overview), see how it's being
// used against its limits (Usage), see what happened to it (Audit). Lifecycle
// actions live in the Overview tab's action bar.

import { useEffect, useState } from 'react';
import { observer } from '@react';
import { useLicense, useI18n } from '@react/hooks';
import { Button, Input, Badge, Alert } from '@react/components';
import { FormDialog } from '@/components/form';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import type {
  GetLicenseAuditLogsResponse,
  GetUsageHistoryResponse,
  GetViolationsResponse,
  License,
} from '@core/types/license';
import { EntitlementsEditor } from './EntitlementsEditor';
import { LicenseStatusBadge, ViolationSeverityBadge, licenseTypeLabel, validationModeLabel } from './badges';
import { bytesToHuman, errMessage, fmtDate, fmtDateTime } from './format';

interface LicenseDetailRailProps {
  license: License;
  onClose: () => void;
  onChanged: (license: License) => void;
  onRemoved: () => void;
}

type Tab = 'overview' | 'usage' | 'audit';

export const LicenseDetailRail = observer(({ license, onClose, onChanged, onRemoved }: LicenseDetailRailProps) => {
  const { t, locale } = useI18n();
  const store = useLicense();

  const [tab, setTab] = useState<Tab>('overview');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [usage, setUsage] = useState<GetUsageHistoryResponse | null>(null);
  const [violations, setViolations] = useState<GetViolationsResponse | null>(null);
  const [audit, setAudit] = useState<GetLicenseAuditLogsResponse | null>(null);

  const [renewOpen, setRenewOpen] = useState(false);
  const [newValidUntil, setNewValidUntil] = useState('');
  const [suspendOpen, setSuspendOpen] = useState(false);
  const [revokeOpen, setRevokeOpen] = useState(false);
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [entitlementsOpen, setEntitlementsOpen] = useState(false);

  const id = license.entity?.id ?? '';

  useEffect(() => {
    if (tab === 'usage' && id) {
      store.GetUsageHistory(id).then(setUsage).catch(() => setUsage(null));
      store.GetViolations(id).then(setViolations).catch(() => setViolations(null));
    }
    if (tab === 'audit' && id) {
      store.GetLicenseAuditLogs(id).then(setAudit).catch(() => setAudit(null));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, id]);

  /** Every lifecycle mutation returns { license }, except UpdateLicenseEntitlements — see below. */
  const run = async (fn: () => Promise<{ license?: License }>, onOk?: () => void) => {
    setBusy(true);
    setError(null);
    try {
      const res = await fn();
      if (res.license) onChanged(res.license);
      onOk?.();
    } catch (e) {
      setError(errMessage(e, t('license.detail.actionError')));
    } finally {
      setBusy(false);
    }
  };

  const saveEntitlements = async () => {
    if (!license.entity) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await store.UpdateLicenseEntitlements(id, { license: license.entity, entitlements: license.entitlements });
      onChanged(updated);
      setEntitlementsOpen(false);
    } catch (e) {
      setError(errMessage(e, t('license.detail.actionError')));
    } finally {
      setBusy(false);
    }
  };

  const reportUsageNow = async () => {
    if (!license.entity) return;
    setBusy(true);
    setError(null);
    try {
      await store.ReportUsage(id, {
        license: license.entity,
        snapshot: {
          entity: undefined,
          license: license.entity,
          active_users: 0,
          total_users: 0,
          active_tenants: 0,
          storage_used_bytes: 0,
          active_nodes: 0,
          product_usage: [],
          is_compliant: true,
          violations: [],
          metadata: {},
        },
      });
      store.GetUsageHistory(id).then(setUsage).catch(() => {});
    } catch (e) {
      setError(errMessage(e, t('license.detail.actionError')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <aside className="w-full shrink-0 space-y-3 overflow-y-auto border-l border-border p-4 lg:w-[26rem]">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-foreground">{t('license.detail.title')}</h2>
        <button onClick={onClose} aria-label={t('common.close')} className="rounded-md p-1 text-muted-foreground hover:bg-muted">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
        </button>
      </div>

      {error && <Alert variant="error">{error}</Alert>}

      <div>
        <div className="flex items-center gap-2">
          <h3 className="text-base font-semibold text-foreground">{license.name}</h3>
          <LicenseStatusBadge status={license.status} />
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          {licenseTypeLabel(t, license.license_type)} · {validationModeLabel(t, license.validation_mode)}
          {license.is_trial && ` · ${t('license.field.isTrial')}`}
        </p>
      </div>

      <div role="tablist" className="flex gap-1 border-b border-border">
        {(['overview', 'usage', 'audit'] as Tab[]).map((v) => (
          <button
            key={v}
            role="tab"
            aria-selected={tab === v}
            onClick={() => setTab(v)}
            className={`-mb-px border-b-2 px-2 py-1.5 text-xs ${tab === v ? 'border-primary font-medium text-foreground' : 'border-transparent text-muted-foreground'}`}
          >
            {t(`license.detail.tab.${v}`)}
          </button>
        ))}
      </div>

      {tab === 'overview' && (
        <div className="space-y-3">
          <div className="flex flex-wrap gap-1.5">
            {license.status === 'LICENSE_STATUS_ACTIVE' && (
              <Button size="sm" variant="danger" onClick={() => setSuspendOpen(true)} isLoading={busy}>{t('license.detail.suspend')}</Button>
            )}
            {license.status === 'LICENSE_STATUS_SUSPENDED' && (
              <Button size="sm" onClick={() => run(() => store.ResumeLicense(id, { license: license.entity! }))} isLoading={busy}>{t('license.detail.resume')}</Button>
            )}
            <Button size="sm" variant="secondary" onClick={() => { setNewValidUntil(license.valid_until?.slice(0, 10) ?? ''); setRenewOpen(true); }}>{t('license.detail.renew')}</Button>
            <Button size="sm" variant="secondary" onClick={() => setEntitlementsOpen(true)}>{t('license.detail.editEntitlements')}</Button>
            <Button size="sm" variant="secondary" onClick={() => setDeactivateOpen(true)}>{t('license.detail.deactivate')}</Button>
            <Button size="sm" variant="danger" onClick={() => setRevokeOpen(true)}>{t('license.detail.revoke')}</Button>
          </div>

          {(suspendOpen || revokeOpen) && (
            <div className="rounded-md border border-border p-2">
              <p className="text-sm font-medium text-foreground">{suspendOpen ? t('license.detail.suspendConfirm') : t('license.detail.revokeConfirm')}</p>
              <Input className="mt-2" value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t('license.detail.reasonPlaceholder')} />
              <div className="mt-2 flex gap-2">
                <Button
                  size="sm"
                  variant="danger"
                  isLoading={busy}
                  onClick={() =>
                    suspendOpen
                      ? run(() => store.SuspendLicense(id, { license: license.entity!, reason }), () => { setSuspendOpen(false); setReason(''); })
                      : run(() => store.RevokeLicense(id, { license: license.entity!, reason }), () => { setRevokeOpen(false); setReason(''); })
                  }
                >
                  {suspendOpen ? t('license.detail.suspend') : t('license.detail.revoke')}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => { setSuspendOpen(false); setRevokeOpen(false); setReason(''); }}>{t('common.cancel')}</Button>
              </div>
            </div>
          )}

          <dl className="space-y-1.5 text-sm">
            <div className="flex justify-between"><dt className="text-muted-foreground">{t('license.field.validFrom')}</dt><dd className="text-foreground">{fmtDate(license.valid_from, locale)}</dd></div>
            <div className="flex justify-between"><dt className="text-muted-foreground">{t('license.field.validUntil')}</dt><dd className="text-foreground">{fmtDate(license.valid_until, locale)}</dd></div>
            {license.grace_period_end && <div className="flex justify-between"><dt className="text-muted-foreground">{t('license.field.gracePeriodEnd')}</dt><dd className="text-foreground">{fmtDate(license.grace_period_end, locale)}</dd></div>}
            <div className="flex justify-between"><dt className="text-muted-foreground">{t('license.field.maxUsers')}</dt><dd className="text-foreground">{license.max_users || t('license.field.unlimited')}</dd></div>
            <div className="flex justify-between"><dt className="text-muted-foreground">{t('license.field.maxTenants')}</dt><dd className="text-foreground">{license.max_tenants || t('license.field.unlimited')}</dd></div>
            <div className="flex justify-between"><dt className="text-muted-foreground">{t('license.field.maxStorage')}</dt><dd className="text-foreground">{license.max_storage_bytes ? bytesToHuman(license.max_storage_bytes) : t('license.field.unlimited')}</dd></div>
            <div className="flex justify-between"><dt className="text-muted-foreground">{t('license.field.maxNodes')}</dt><dd className="text-foreground">{license.max_nodes || t('license.field.unlimited')}</dd></div>
            {license.last_heartbeat_at && <div className="flex justify-between"><dt className="text-muted-foreground">{t('license.field.lastHeartbeat')}</dt><dd className="text-foreground">{fmtDateTime(license.last_heartbeat_at, locale)}</dd></div>}
          </dl>

          {license.hardware_fingerprint?.hostname && (
            <div className="rounded-md border border-border p-2 text-xs">
              <p className="text-muted-foreground">{t('license.field.hardwareFingerprint')}</p>
              <p className="mt-0.5 truncate text-foreground">{license.hardware_fingerprint.hostname}</p>
            </div>
          )}

          {license.entitlements?.products && license.entitlements.products.length > 0 && (
            <div>
              <p className="text-xs uppercase text-muted-foreground">{t('license.entitlements.products')}</p>
              <ul className="mt-1 space-y-1">
                {license.entitlements.products.map((p) => (
                  <li key={p.product_slug} className="flex items-center justify-between text-sm">
                    <span className="text-foreground">{p.product_name || p.product_slug}</span>
                    <Badge variant={p.is_enabled ? 'success' : 'default'}>{p.plan_slug || (p.is_enabled ? t('common.active') : t('common.inactive'))}</Badge>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {tab === 'usage' && (
        <div className="space-y-3">
          <div>
            <h4 className="text-sm font-medium text-foreground">{t('license.detail.violations')}</h4>
            {violations && violations.violations.length > 0 ? (
              <ul className="mt-1 space-y-1">
                {violations.violations.map((v, i) => (
                  <li key={i} className="rounded-md border border-border p-2 text-xs">
                    <div className="flex items-center justify-between">
                      <span className="text-foreground">{v.description || v.violation_type}</span>
                      <ViolationSeverityBadge severity={v.severity} />
                    </div>
                    <p className="mt-0.5 text-muted-foreground">{v.current_value} / {v.allowed_value}</p>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-1 text-sm text-muted-foreground">{t('license.detail.compliant')}</p>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium text-foreground">{t('license.detail.usageHistory')}</h4>
              <Button size="sm" variant="secondary" onClick={reportUsageNow} isLoading={busy}>{t('license.detail.reportNow')}</Button>
            </div>
            <ul className="mt-1 space-y-1">
              {(usage?.snapshots ?? []).map((s, i) => (
                <li key={i} className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>{fmtDateTime(s.created_at, locale)}</span>
                  <span>{s.active_users} {t('license.detail.users')} · {bytesToHuman(s.storage_used_bytes)}</span>
                </li>
              ))}
              {(!usage || usage.snapshots.length === 0) && <p className="text-xs text-muted-foreground">{t('license.detail.noUsage')}</p>}
            </ul>
          </div>
        </div>
      )}

      {tab === 'audit' && (
        <ul className="space-y-1.5">
          {(audit?.logs ?? []).map((log, i) => (
            <li key={i} className="rounded-md border border-border p-2 text-xs">
              <div className="flex items-center justify-between">
                <span className="font-medium text-foreground">{log.action?.replace('LICENSE_AUDIT_ACTION_', '') ?? '—'}</span>
                <span className="text-muted-foreground">{fmtDateTime(log.created_at, locale)}</span>
              </div>
              {log.actor?.display_name && <p className="mt-0.5 text-muted-foreground">{log.actor.display_name}</p>}
            </li>
          ))}
          {(!audit || audit.logs.length === 0) && <p className="text-sm text-muted-foreground">{t('license.detail.noAudit')}</p>}
        </ul>
      )}

      {/* Renew */}
      <FormDialog
        open={renewOpen}
        onClose={() => setRenewOpen(false)}
        onSubmit={() => run(() => store.RenewLicense(id, { license: license.entity!, new_valid_until: new Date(newValidUntil).toISOString() }), () => setRenewOpen(false))}
        submitLabel={t('common.save')}
        isSubmitting={busy}
        dirty={false}
        width="md"
        aria-label={t('license.detail.renew')}
      >
        <div className="px-3.5 pb-3 pt-3">
          <label htmlFor="license-renew-date" className="mb-1 block text-sm text-muted-foreground">{t('license.field.validUntil')}</label>
          <input
            id="license-renew-date"
            type="date"
            value={newValidUntil}
            onChange={(e) => setNewValidUntil(e.target.value)}
            className="rounded-md border border-input bg-background px-2.5 py-1.5 text-sm text-foreground outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
      </FormDialog>

      {/* Edit entitlements */}
      <FormDialog
        open={entitlementsOpen}
        onClose={() => setEntitlementsOpen(false)}
        onSubmit={saveEntitlements}
        submitLabel={t('common.save')}
        isSubmitting={busy}
        dirty={false}
        width="lg"
        aria-label={t('license.detail.editEntitlements')}
      >
        <div className="px-3.5 pb-3 pt-3">
          <EntitlementsEditor
            value={license.entitlements ?? { products: [], features: [] }}
            onChange={(next) => onChanged({ ...license, entitlements: next })}
          />
        </div>
      </FormDialog>

      <ConfirmDialog
        open={deactivateOpen}
        title={t('license.detail.deactivate')}
        description={t('license.detail.deactivateConfirm')}
        isConfirming={busy}
        onCancel={() => setDeactivateOpen(false)}
        onConfirm={async () => {
          setBusy(true);
          setError(null);
          try {
            await store.DeactivateLicense(id, { license: license.entity!, reason });
            setDeactivateOpen(false);
            onRemoved();
          } catch (e) {
            setError(errMessage(e, t('license.detail.actionError')));
          } finally {
            setBusy(false);
          }
        }}
      />
    </aside>
  );
});
